import type { CharacterGridSpec } from "@/character/grid";
import { normalizeGridSpec } from "@/character/grid";
import {
  attackAnimationFrameRange,
  idleAnimationFrameRange,
} from "@/character/spritesheet";
import { HOME_CELL_PX } from "@/home/grid";
import { normalizeImageDataUrl } from "@/home/image-data-url";
import { normalizePartKind } from "@/home/part-kind";
import type { HomePartDefinition } from "@/home/storage";
import { getGameSettings } from "@/game/model/game-settings";
import { addSkillSpritesheetTexture } from "@/game/load-data-texture";
import type Phaser from "phaser";

export function partSpritesheetTextureKey(partId: string): string {
  return `home-part-sheet-${partId}`;
}

export function partMonsterIdleAnimKey(partId: string): string {
  return `home-part-monster-idle-${partId}`;
}

export function partMonsterAttackAnimKey(partId: string): string {
  return `home-part-monster-attack-${partId}`;
}

export function partHasMonsterSheet(part: HomePartDefinition): boolean {
  return (
    normalizePartKind(part.kind) === "monster" &&
    typeof part.spritesheetDataUrl === "string" &&
    part.spritesheetDataUrl.startsWith("data:") &&
    Boolean(part.grid)
  );
}

export function ensurePartMonsterAnimations(
  scene: Phaser.Scene,
  partId: string,
  gridInput: CharacterGridSpec,
): void {
  const grid = normalizeGridSpec(gridInput);
  const texKey = partSpritesheetTextureKey(partId);
  if (!scene.textures.exists(texKey)) return;

  const idleKey = partMonsterIdleAnimKey(partId);
  if (!scene.anims.exists(idleKey)) {
    const { start, end } = idleAnimationFrameRange(grid);
    scene.anims.create({
      key: idleKey,
      frames: scene.anims.generateFrameNumbers(texKey, { start, end }),
      frameRate: 8,
      repeat: -1,
    });
  }

  const attackKey = partMonsterAttackAnimKey(partId);
  if (!scene.anims.exists(attackKey)) {
    const { start, end } = attackAnimationFrameRange(grid);
    scene.anims.create({
      key: attackKey,
      frames: scene.anims.generateFrameNumbers(texKey, { start, end }),
      frameRate: getGameSettingsAttackFps(scene),
      repeat: 0,
    });
  }
}

function getGameSettingsAttackFps(_scene: Phaser.Scene): number {
  return getGameSettings().playerAttackAnimFps;
}

export async function registerPartMonsterSheet(
  scene: Phaser.Scene,
  part: HomePartDefinition,
): Promise<void> {
  if (!partHasMonsterSheet(part) || !part.grid) return;
  const grid = normalizeGridSpec(part.grid);
  const key = partSpritesheetTextureKey(part.id);
  await addSkillSpritesheetTexture(
    scene.textures,
    key,
    normalizeImageDataUrl(part.spritesheetDataUrl!),
    grid.frameWidth,
    grid.frameHeight,
  );
  ensurePartMonsterAnimations(scene, part.id, grid);
}

export function fitMonsterSpriteInFootprint(
  sprite: Phaser.GameObjects.Sprite,
  grid: CharacterGridSpec,
  _cellsW: number,
  cellsH: number,
): void {
  const targetH = cellsH * HOME_CELL_PX;
  const scale = targetH / grid.frameHeight;
  sprite.setScale(scale);
  sprite.setOrigin(0.5, 1);
}
